/**
 * 메일 발송 직후, 직원/내연락처에 없던 외부 주소를 새 연락처로 등록할지 묻는 prompt.
 *
 * <h4>흐름</h4>
 * <ol>
 *   <li>외부 주소 목록 표시 — 각 항목마다 + 버튼</li>
 *   <li>+ 누르면 이름 입력 모달 (작은 자식 모달) 띄움</li>
 *   <li>이름 입력 후 등록 버튼 → mailApi.createContact 호출</li>
 *   <li>성공 시 체크 표시. 모든 항목 등록되면 prompt 자동 닫힘.</li>
 * </ol>
 */
import React, { useEffect, useState } from "react";
import {
    Box, IconButton, List, ListItem, ListItemText, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import CheckRoundedIcon     from "@mui/icons-material/CheckRounded";

import { ds }      from "../../../../styles/designSystem";
import { mailApi } from "../../api/mailApi";
import { notify }  from "../../../../util/toast";
import FormModal   from "../../../../component/common/FormModal";

export default function RegisterContactsPrompt({ open, candidates = [], onClose }) {
    const [registered, setRegistered] = useState(new Set());
    const [busy, setBusy] = useState(false);

    // 이름 입력 단계 — null 이면 닫힘, { email, name } 이면 자식 모달 열림
    const [editing, setEditing] = useState(null);

    // prompt 닫힐 때 내부 상태도 리셋 (다음 발송 시 재사용 대비)
    useEffect(() => {
        if (!open) {
            setRegistered(new Set());
            setEditing(null);
            setBusy(false);
        }
    }, [open]);

    const startEdit = (cand) => {
        if (registered.has(cand.email)) return;
        setEditing({ email: cand.email, name: cand.name || "" });
    };

    const cancelEdit = () => setEditing(null);

    const confirmRegister = async () => {
        if (!editing?.email) return;
        const trimmedName = (editing.name || "").trim();
        if (!trimmedName) {
            notify("이름을 입력하세요", "warning");
            return;
        }
        setBusy(true);
        try {
            await mailApi.createContact({
                name: trimmedName,
                email: editing.email,
            });
            const justRegisteredEmail = editing.email;
            setRegistered(prev => {
                const next = new Set(prev);
                next.add(justRegisteredEmail);
                return next;
            });
            notify(`${justRegisteredEmail} 등록 완료`, "success");
            setEditing(null);

            // 남은 후보가 없으면 prompt 자체 닫기
            const remaining = candidates.filter(
                c => !registered.has(c.email) && c.email !== justRegisteredEmail
            );
            if (remaining.length === 0) onClose?.();
        } catch (e) {
            notify(e?.response?.data?.message || "등록 실패", "error");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <FormModal
                open={open}
                onClose={onClose}
                title="새 연락처로 등록하시겠습니까?"
                subtitle="이번 메일에서 처음 사용한 외부 주소입니다."
                maxWidth="xs"
                cancelLabel="닫기"
            >
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mb: 1 }}>
                    "+" 버튼을 누르면 이름 입력 창이 열립니다.
                    회사·직책·메모 같은 상세 정보는 좌측 사이드바의 <b>내 연락처</b>에서 따로 수정하세요.
                </Typography>
                <List dense disablePadding>
                    {candidates.map((c, i) => {
                        const done = registered.has(c.email);
                        return (
                            <ListItem
                                key={i}
                                sx={{
                                    px: 1, py: 0.5, borderRadius: 1.2,
                                    opacity: done ? 0.6 : 1,
                                    "&:hover": { bgcolor: done ? "transparent" : "#f8fafc" },
                                }}
                                secondaryAction={
                                    done ? (
                                        <Tooltip title="등록 완료">
                                            <CheckRoundedIcon sx={{ fontSize: 18, color: "#16a34a", mr: 1 }} />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="이름을 입력해 새 연락처로 등록">
                                            <span>
                                                <IconButton size="small" edge="end"
                                                    onClick={() => startEdit(c)}
                                                    sx={{ color: ds.colors.primary }}>
                                                    <PersonAddRoundedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )
                                }>
                                <ListItemText
                                    primary={
                                        <Box sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                                            {c.name || c.email}
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ fontSize: 11, color: "#64748b" }}>{c.email}</Box>
                                    }
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </FormModal>

            {/* 자식 모달 — 이름 입력 후 등록 */}
            <FormModal
                open={!!editing}
                onClose={cancelEdit}
                title="새 연락처 등록"
                subtitle={editing?.email || ""}
                maxWidth="xs"
                onSave={confirmRegister}
                saveLabel="+ 등록"
                saving={busy}
                saveDisabled={busy || !((editing?.name || "").trim())}
            >
                <Stack spacing={1.5}>
                    <Box>
                        <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5, fontWeight: 600 }}>
                            이름 <span style={{ color: "#dc2626" }}>*</span>
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder="연락처에 표시될 이름"
                            value={editing?.name || ""}
                            onChange={(e) => setEditing(prev => prev ? { ...prev, name: e.target.value } : prev)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !busy && (editing?.name || "").trim()) {
                                    confirmRegister();
                                }
                            }}
                        />
                    </Box>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        등록 후엔 좌측 사이드바의 <b>내 연락처</b>에서 회사·직책·메모 등을 추가할 수 있어요.
                    </Typography>
                </Stack>
            </FormModal>
        </>
    );
}
